import {quizActions, useQuiz} from "../../providers/QuizProvider.tsx";

interface QuizNavigationProps {
    totalSteps: number;
    onSubmit?: () => void;
    validateStep?: () => boolean;
    nextButtonText?: string;
    canProceed?: boolean;
    validationMessage?: string;
    hidePreviousButton?: boolean;
}

const QuizNavigation = ({
                            totalSteps,
                            onSubmit,
                            validateStep,
                            nextButtonText,
                            canProceed = true,
                            validationMessage,
                            hidePreviousButton = false
                        }: QuizNavigationProps) => {
    const {state, dispatch} = useQuiz();
    const isFirstStep = state.currentStep === 0;
    const isLastStep = state.currentStep === totalSteps - 1;
    const isDisabled = !canProceed || state.isSubmitting;

    const handleNext = () => {
        if (!canProceed) {
            return;
        }

        if (validateStep && !validateStep()) {
            return;
        }

        if (isLastStep && onSubmit) {
            onSubmit();
        } else {
            dispatch(quizActions.nextStep());
        }
    };

    const handlePrevious = () => {
        dispatch(quizActions.prevStep());
    };

    const getButtonText = () => {
        if (state.isSubmitting) return 'Submitting...';
        if (nextButtonText) return nextButtonText;
        if (isLastStep) return 'Submit & View Provider';
        return 'Next';
    };

    return (
        <>
            {/* Validation Message - Desktop only */}
            {!canProceed && validationMessage && (
                <div className="hidden md:block mt-8 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="currentColor"
                             viewBox="0 0 20 20">
                            <path fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                  clipRule="evenodd"/>
                        </svg>
                        <p className="text-sm text-red-800 font-medium">{validationMessage}</p>
                    </div>
                </div>
            )}

            {/* Desktop Navigation */}
            <div className={`hidden md:flex items-center mt-8 pt-6 border-t border-gray-200 ${hidePreviousButton ? 'justify-end' : 'justify-between'}`}>
                {!hidePreviousButton && (
                    <button
                        type="button"
                        onClick={handlePrevious}
                        disabled={isFirstStep}
                        className={`px-6 py-3 rounded-md font-medium transition-all ${
                            isFirstStep
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                        Previous
                    </button>
                )}

                {totalSteps > 1 && (
                    <div className="flex gap-2">
                        {Array.from({length: totalSteps}).map((_, index) => (
                            <div
                                key={index}
                                className={`h-1.5 w-4 rounded-full transition-all ${
                                    index === state.currentStep
                                        ? 'bg-blue-600'
                                        : index < state.currentStep
                                            ? 'bg-green-500'
                                            : 'bg-gray-300'
                                }`}
                            />
                        ))}
                    </div>
                )}

                <button
                    type="button"
                    onClick={handleNext}
                    disabled={isDisabled}
                    className={`px-6 py-3 rounded-md font-medium transition-all ${
                        isDisabled
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg'
                    }`}
                >
                    {getButtonText()}
                </button>
            </div>

            {/* Mobile Sticky Navigation */}
            <div
                className="md:hidden sticky bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 shadow-lg z-40 -mx-8 px-4 py-4 mt-16">
                {/* Validation Message in mobile bar */}
                {!canProceed && validationMessage && (
                    <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start gap-2">
                            <svg className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" fill="currentColor"
                                 viewBox="0 0 20 20">
                                <path fillRule="evenodd"
                                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                      clipRule="evenodd"/>
                            </svg>
                            <p className="text-xs text-red-800 font-medium">{validationMessage}</p>
                        </div>
                    </div>
                )}

                <div className={`flex items-center gap-4 ${hidePreviousButton ? 'justify-end' : 'justify-between'}`}>
                    {!hidePreviousButton && (
                        <button
                            type="button"
                            onClick={handlePrevious}
                            disabled={isFirstStep}
                            className={`px-4 py-3 rounded-md font-medium transition-all flex-shrink-0 ${
                                isFirstStep
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            Previous
                        </button>
                    )}

                    <button
                        type="button"
                        onClick={handleNext}
                        disabled={isDisabled}
                        className={`px-4 py-3 rounded-md font-medium transition-all flex-shrink-0 ${
                            isDisabled
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'
                        } ${hidePreviousButton ? 'w-full' : ''}`}
                    >
                        {getButtonText()}
                    </button>
                </div>
            </div>
        </>
    );
};

export default QuizNavigation;